/**
 * signinHosts — the CLIENT twin of the sign-in host predicates in
 * `functions/src/composio/signinDestinationProbe.ts` (contract C5,
 * PLAN-non-direct-app-connections P0, 2026-09-02).
 *
 * DATA + PURE PREDICATES ONLY. Nothing here decides a lane, a method or a
 * destination (MASTER RULE) — the client renders and the VM/server judge;
 * these predicates exist so every surface that must tell "the app's own
 * front door" from "off-app" (the search-engine belt, the pilot's landing
 * check) reads the SAME host lists the server's probe and the workstation's
 * `signin-classify.ts` use. Every regex literal and the tenant-IdP list are
 * byte-equal to the server module; `tests/onboarding/signinHosts.test.ts`
 * pins the parity (the vm-classifier bucket extends it to the VM copy).
 *
 * Do not add logic here. Do not import anything here.
 */

/** Auth-surface host prefix — accounts.google.com IS the sign-in page for
 *  every Google-SSO app (the 2026-08-22 Fitbit incident). */
export const AUTH_HOST_PREFIX = /^(accounts|login|signin|sign-in|auth|sso|id|idp)\./i;

/** Tenant identity providers: a sign-in that lands on acme.okta.com or
 *  contoso.b2clogin.com has redirected to its enterprise SSO — a SUCCESS,
 *  never "off-app". Registrable domains. */
export const TENANT_IDP_DOMAINS = [
  'okta.com',
  'auth0.com',
  'pingone.com',
  'onelogin.com',
  'duosecurity.com',
  'b2clogin.com',
] as const;
/** Alias of TENANT_IDP_DOMAINS under the implementation brief's name. */
export const TENANT_IDP_REGISTRABLE_DOMAINS = TENANT_IDP_DOMAINS;

/** Search-engine hosts — mirror of signin-classify.ts SEARCH_ENGINE_HOST. */
export const SEARCH_ENGINE_HOST =
  /(^|\.)((www|search)\.)?(google|bing|duckduckgo|yahoo|baidu|ecosia|startpage)\.[a-z.]+$/i;

/** ccTLD-aware registrable tail: under co.uk / com.au-style suffixes a
 *  2-label tail is just the suffix. */
export const MULTI_LABEL_SUFFIX = /^(co|com|org|net|gov|ac|edu)\.[a-z]{2}$/;

/** Hostname of a URL, or the input itself when it is already a bare host. */
export function hostOf(urlOrHost: string): string {
  const raw = String(urlOrHost || '').trim();
  if (!raw) return '';
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) {
    try {
      return new URL(raw).hostname.toLowerCase();
    } catch {
      return '';
    }
  }
  return raw.toLowerCase().split('/')[0].split(':')[0];
}

export function registrableDomain(host: string): string {
  const parts = String(host || '').toLowerCase().split('.').filter(Boolean);
  const two = parts.slice(-2).join('.');
  return MULTI_LABEL_SUFFIX.test(two) ? parts.slice(-3).join('.') : two;
}

export function isTenantIdpHost(urlOrHost: string): boolean {
  const host = hostOf(urlOrHost);
  if (!host) return false;
  return (TENANT_IDP_DOMAINS as readonly string[]).includes(registrableDomain(host));
}

/** Auth surface: the prefix family OR a tenant IdP domain. */
export function isAuthHost(urlOrHost: string): boolean {
  const host = hostOf(urlOrHost);
  if (!host) return false;
  return AUTH_HOST_PREFIX.test(host) || isTenantIdpHost(host);
}

/** A search page is NEVER a sign-in destination; auth surfaces on those
 *  registrable domains (accounts.google.com) are exempt. */
export function isSearchEngineHost(urlOrHost: string): boolean {
  const host = hostOf(urlOrHost);
  if (!host) return false;
  if (AUTH_HOST_PREFIX.test(host)) return false;
  return SEARCH_ENGINE_HOST.test(host);
}
